import type { ClientData, MeasureData } from '../interfaces';
import { calculateBodyFat } from '../dashboard/calculate-bf';

/** A scan older than this means the client has gone quiet. */
export const OVERDUE_DAYS = 28;
/** A scan inside this window is still "new" to the coach. */
export const RECENT_DAYS = 7;
/** Within this much of target counts as reached. */
export const GOAL_TOLERANCE_POINTS = 0.5;
/** The window the reference deck's activity panel reports over. */
export const ACTIVITY_DAYS = 30;

export type AttentionReason =
    | 'goal-reached'
    | 'scan-to-review'
    | 'overdue'
    | 'never-scanned';

export interface RosterRow {
    id: string;
    name: string;
    initials: string;
    gender: 'male' | 'female';
    scanCount: number;
    daysSinceScan: number | null;
    startBodyFat: number | null;
    nowBodyFat: number | null;
    waistCm: number | null;
    targetBodyFat: number | null;
    reason: AttentionReason | null;
    /** Lower sorts first. */
    priority: number;
}

export interface OverviewKpis {
    activeClients: number;
    newThisMonth: number;
    needsAttention: number;
    scannedRecently: number;
    goalsReached: number;
    /** Scans taken across the whole roster in the last ACTIVITY_DAYS. */
    scansInWindow: number;
}

/**
 * Why a client is in the queue, and how urgently.
 *
 * The deck's slide 3 asks the overview to answer "Vem behöver mig nu?" in ten
 * seconds, so the ordering is the feature — a list sorted by name answers
 * nothing. Order is by what the coach can act on:
 *
 *   1. goal reached     — the conversation that cannot wait, and the one a
 *                         client notices being missed
 *   2. scan to review   — work already sitting in front of the coach
 *   3. overdue          — going quiet, and the coach may not have noticed
 *   4. never scanned    — invited and never started
 *
 * "Scan to review" is not a guess. A measurement carries
 * `confirmedMeasurements` once a coach has been through it, so its absence on
 * the latest scan is exactly "this has not been looked at" — and the action it
 * offers opens the same review dialog that sets it.
 */
const classify = (
    row: Omit<RosterRow, 'reason' | 'priority'>,
    latestConfirmed: boolean,
): { reason: AttentionReason | null; priority: number } => {
    const { scanCount, daysSinceScan, nowBodyFat, targetBodyFat } = row;

    if (scanCount === 0) return { reason: 'never-scanned', priority: 4 };

    if (
        targetBodyFat !== null &&
        nowBodyFat !== null &&
        nowBodyFat <= targetBodyFat + GOAL_TOLERANCE_POINTS
    ) {
        return { reason: 'goal-reached', priority: 1 };
    }

    if (!latestConfirmed && daysSinceScan !== null && daysSinceScan <= RECENT_DAYS) {
        return { reason: 'scan-to-review', priority: 2 };
    }

    if (daysSinceScan !== null && daysSinceScan > OVERDUE_DAYS) {
        return { reason: 'overdue', priority: 3 };
    }

    return { reason: null, priority: 99 };
};

const daysBetween = (a: Date, b: Date) =>
    Math.floor((a.getTime() - b.getTime()) / 86_400_000);

/**
 * Body fat as the dashboard shows it — recomputed from the tape, never the
 * stored value. `loadModels` does the same thing on the client page, so a
 * figure here would otherwise disagree with the one a coach sees on opening
 * the client.
 */
const bodyFatOf = (m: MeasureData | undefined, gender: 'male' | 'female') => {
    if (!m) return null;
    const v = calculateBodyFat(m.measurements, gender, 'cm');
    return Number.isFinite(v) && v > 0 ? v : null;
};

export function buildOverview(
    profiles: ClientData[],
    /** Measurements per profile ID. Absent means "not loaded yet", not "none". */
    measurements: Map<string, MeasureData[]>,
    now: Date = new Date(),
    /**
     * True scan counts, when the caller knows them without holding every scan.
     * GET /profile-summaries returns first and latest only, so the map above
     * has two entries for a client with twelve scans.
     */
    scanCounts?: Map<string, number>,
    /**
     * Whether this deployment has body fat targets. Off (retail), a target
     * left on a profile is ignored, so nobody is queued as "Target reached".
     */
    options: { goals?: boolean } = {},
): { kpis: OverviewKpis; queue: RosterRow[]; rows: RosterRow[] } {
    const goals = options.goals ?? true;
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const rows: RosterRow[] = profiles.map((p) => {
        const gender: 'male' | 'female' = p.gender === 'female' ? 'female' : 'male';
        const scans = [...(measurements.get(p.ID) ?? [])].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        const first = scans[0];
        const latest = scans[scans.length - 1];

        // lastMeasure is on the profile, so recency is known before any
        // measurement has loaded. That is what lets the queue render on the
        // first paint rather than after N requests.
        const lastScanAt = p.lastMeasure
            ? new Date(p.lastMeasure)
            : latest
              ? new Date(latest.createdAt)
              : null;

        const base = {
            id: p.ID,
            name: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.email || 'Unnamed',
            initials: `${p.firstName?.[0] ?? ''}${p.lastName?.[0] ?? ''}`.toUpperCase(),
            gender,
            scanCount: scanCounts?.get(p.ID) ?? scans.length,
            daysSinceScan: lastScanAt ? daysBetween(now, lastScanAt) : null,
            startBodyFat: bodyFatOf(first, gender),
            nowBodyFat: bodyFatOf(latest, gender),
            waistCm: latest
                ? (gender === 'female'
                      ? latest.measurements.waist_female
                      : latest.measurements.waist) || null
                : null,
            targetBodyFat: goals ? (p.target?.measurements?.body_fat ?? null) : null,
        };

        // Absent confirmedMeasurements means nobody has been through this scan.
        const latestConfirmed = Boolean(
            latest?.confirmedMeasurements &&
                Object.keys(latest.confirmedMeasurements).length > 0,
        );

        return { ...base, ...classify(base, latestConfirmed) };
    });

    const queue = rows
        .filter((r) => r.reason !== null)
        .sort((a, b) => a.priority - b.priority || (b.daysSinceScan ?? 0) - (a.daysSinceScan ?? 0));

    const windowStart = new Date(now.getTime() - ACTIVITY_DAYS * 86_400_000);
    // Scans, not clients-who-scanned: the deck's panel counts measurements,
    // and a client scanning twice in the window is two.
    const scansInWindow = [...measurements.values()]
        .flat()
        .filter((m) => new Date(m.createdAt) >= windowStart).length;
    // Note this undercounts against a summary feed, which carries first and
    // latest only. The caller that uses one says so in the panel's caption.

    return {
        kpis: {
            scansInWindow,
            activeClients: profiles.length,
            newThisMonth: profiles.filter(
                (p) => p.createdAt && new Date(p.createdAt) >= monthStart,
            ).length,
            needsAttention: queue.length,
            scannedRecently: rows.filter(
                (r) => r.daysSinceScan !== null && r.daysSinceScan <= RECENT_DAYS,
            ).length,
            goalsReached: rows.filter((r) => r.reason === 'goal-reached').length,
        },
        queue,
        rows,
    };
}
